import { useEffect, useRef } from "react";
import uPlot, { type Options as UPlotOptions } from "uplot";

import type { NavRow } from "../lib/types";

type Props = {
  rows: NavRow[];
  height?: number;
};

/**
 * NAV vs SPY, both normalized to 100 at the first sample.
 * uPlot is the right choice here — 30-day NAV history is ~30 points,
 * tiny payload, snappy redraws, and the legend tooltip is built-in.
 */
export function NavChart({ rows, height = 280 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (rows.length < 2) {
      // Not enough data — destroy any prior plot and bail.
      plotRef.current?.destroy();
      plotRef.current = null;
      return;
    }

    const xs = rows.map((r) => Date.parse(r.trading_day_et) / 1000);
    const nav0 = rows[0]!.nav_cents;
    const spy0 = rows[0]!.spy_close_cents;
    const navs = rows.map((r) => (r.nav_cents / nav0) * 100);
    const spys = rows.map((r) => (r.spy_close_cents / spy0) * 100);

    const opts: UPlotOptions = {
      width: containerRef.current.clientWidth,
      height,
      legend: { show: true },
      cursor: { drag: { x: true, y: false } },
      scales: { x: { time: true }, y: { auto: true } },
      series: [
        { label: "date" },
        {
          label: "strategy (norm)",
          stroke: "#10b981",
          width: 2,
          value: (_u, v) => (v == null ? "—" : v.toFixed(2)),
        },
        {
          label: "SPY (norm)",
          stroke: "#64748b",
          width: 1.5,
          dash: [4, 4],
          value: (_u, v) => (v == null ? "—" : v.toFixed(2)),
        },
      ],
      axes: [
        { stroke: "#94a3b8", grid: { stroke: "#1f2937", width: 1 } },
        { stroke: "#94a3b8", grid: { stroke: "#1f2937", width: 1 } },
      ],
    };

    if (plotRef.current) plotRef.current.destroy();
    plotRef.current = new uPlot(opts, [xs, navs, spys], containerRef.current);

    const onResize = () => {
      if (!containerRef.current || !plotRef.current) return;
      plotRef.current.setSize({
        width: containerRef.current.clientWidth,
        height,
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      plotRef.current?.destroy();
      plotRef.current = null;
    };
  }, [rows, height]);

  if (rows.length < 2) {
    return (
      <div
        className="flex h-32 items-center justify-center rounded-md border border-dashed border-ink-700 text-sm text-slate-500"
        style={{ minHeight: 128 }}
      >
        need at least 2 NAV points to chart
      </div>
    );
  }
  return <div ref={containerRef} style={{ width: "100%", height }} />;
}
